import styled from "styled-components";
import { Input, InputNumber } from "antd";
import { RATypography } from "../Typography";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { RAImage } from "../Image";
import { IconArrowDown, IconArrowUp } from "@/assets/images";

const { TextArea, Password } = Input;

export const RATextInput = ({
	value,
	onChange,
	addonAfter,
	onCountryChange,
	prefix,
	suffix,
	width = "100%",
	height = "52px",
	placeholder = "Enter ...",
	title,
	maxLength,
	type = "text", // text - number - textarea - phone - email - password
	min = 0,
	max,
	defaultValue,
	defaultCountry = "US",
	formatter,
	parser,
	country,
	error,
	disabled = false,
	minRows = 3,
	maxRows = 5,
	"data-testid": dataTestId = null,
	...props
}) => {
	const _renderInputByType = () => {
		if (type === "textarea") {
			return (
				<TextArea
					placeholder={placeholder}
					prefix={prefix}
					onChange={(e) => onChange(e.target.value)}
					value={value}
					suffix={suffix}
					style={{ width: width, height: height }}
					maxLength={maxLength || null}
					autoSize={{ minRows: minRows, maxRows: maxRows }}
					disabled={disabled}
					data-testid={dataTestId}
				/>
			);
		}

		if (type === "password") {
			return (
				<Password
					placeholder={placeholder}
					prefix={prefix}
					onChange={(e) => onChange(e.target.value)}
					value={value}
					suffix={suffix}
					style={{ width: width, height: height }}
					disabled={disabled}
					data-testid={dataTestId}
					{...props}
				/>
			);
		}

		if (type === "number") {
			return (
				<InputNumber
					value={value}
					defaultValue={defaultValue}
					prefix={prefix}
					placeholder={placeholder}
					min={min}
					max={max}
					formatter={formatter}
					parser={parser}
					onChange={onChange}
					addonAfter={addonAfter}
					onWheel={(e) => e.target?.blur?.()}
					disabled={disabled}
					type="number"
					controls={{
						upIcon: <RAImage width="12px" height="12px" url={IconArrowUp} />,
						downIcon: <RAImage width="12px" height="12px" url={IconArrowDown} />,
					}}
					style={{
						width: width,
						height: height,
						alignItems: "center",
					}}
					data-testid={dataTestId}
					{...props}
				/>
			);
		}

		if (type === "phone") {
			return (
				<PhoneInput
					placeholder={placeholder || "Enter phone number"}
					onChange={onChange}
					onCountryChange={onCountryChange}
					value={value}
					country={country}
					defaultCountry={defaultCountry}
					limitMaxLength={true}
					disabled={disabled}
				/>
			);
		}

		return (
			<Input
				placeholder={placeholder}
				prefix={prefix}
				onChange={(e) => onChange?.(e.target.value)}
				value={value}
				suffix={suffix}
				style={{ width: width, height: height }}
				disabled={disabled}
				data-testid={dataTestId}
				{...props}
			/>
		);
	};

	return (
		<WrapperInput>
			{title && (
				<WrapperTitle>
					<RATypography color="#595959" variant="sub-heading4">
						{title}
					</RATypography>
				</WrapperTitle>
			)}
			{_renderInputByType()}
			{!!error && (
				<RATypography variant="body3" color="#BF1A2F" top="4px">
					{error}
				</RATypography>
			)}
		</WrapperInput>
	);
};

const WrapperInput = styled.div`
	.ant-input,
	.ant-input-status-success {
		background: transparent;
		outline: 0;
	}

	.ant-input {
		padding: 10px 20px;
		border-color: #e9e8e8;
		&:hover {
			outline: none;
			border-color: #ffb11a;
		}
		&:focus {
			outline: none;
			border-color: #ffb11a;
			box-shadow: 0px 0px 2px #ffb11a;
		}
	}
	.ant-input-affix-wrapper {
		padding: 10px 20px;
		border-color: #e9e8e8;
		&:hover {
			outline: none;
			border-color: #ffb11a;
		}
		&:focus {
			outline: none;
			border-color: #ffb11a;
			box-shadow: 0px 0px 2px #ffb11a;
		}
	}
	.ant-input-number {
		border-color: #e9e8e8;
	}
	.ant-input-number-affix-wrapper {
		padding-left: 20px;
		border-color: #e9e8e8;
		&:hover {
			outline: none;
			border-color: #ffb11a;
		}
		&:focus {
			outline: none;
			border-color: #ffb11a;
			box-shadow: 0px 0px 2px #ffb11a;
		}
		.ant-input-number .ant-input-number-input {
			height: 52px;
			padding: 0 4px;
		}
	}
	.ant-input-number-affix-wrapper > div.ant-input-number {
		height: 100%;
		display: flex;
		align-items: center;
	}
	.ant-input-number .ant-input-number-input {
		height: 52px;
		padding: 0 20px;
		border-color: #e9e8e8;
	}
	.PhoneInputCountry {
		border-right: 1px solid #eee;
		padding-right: 8px;
	}
	.PhoneInputCountrySelectArrow {
		margin-left: 10px;
		margin-right: 8px;
	}
	.PhoneInput {
		height: 52px;
		padding-left: 20px;
		padding-right: 20px;
		border: 1px solid #e9e8e8;
		border-radius: 12px;

		&:hover {
			outline: none;
			border-color: #ffb11a;
		}
		&:focus {
			outline: none;
			border-color: #ffb11a;
		}
		&:focus-within {
			outline: none;
			border-color: #ffb11a;
		}

		.PhoneInputInput {
			border: none !important;
			border-left: 1px solid #e9e8e8;
			padding: 10px 10px;
			font-size: 14px;
			color: #222;
			&:focus-visible {
				outline: none;
				border-color: none;
			}
		}
		.PhoneInputInput::placeholder {
			color: rgba(0, 0, 0, 0.25);
			font-size: 14px;
			list-style: none;
			font-family: DM Sans;
		}
	}

	input:-webkit-autofill {
		-webkit-background-clip: text;
	}
`;
const WrapperTitle = styled.div`
	padding-bottom: 8px;
`;
